/**
 * History management utilities for canvas undo/redo functionality
 * Extracted from MyCanvas.js to reduce file size
 */

import { flushSync } from "react-dom";
import { normalizeArrowCoordinates } from "./canvasUtils";
import { createStateHash } from "./keyboardStateUtils";

// Round for content comparison so Konva/float differences don't prevent merging add_image + update_image_properties
const round = (v) => (typeof v === "number" && !Number.isNaN(v) ? Math.round(v * 100) / 100 : v);
// Stable stringify for cropData so key order doesn't cause false differences
const stableCropKey = (crop) => {
  if (!crop || typeof crop !== "object") return "";
  try {
    return JSON.stringify(Object.keys(crop).sort().reduce((acc, k) => ({ ...acc, [k]: crop[k] }), {}));
  } catch {
    return JSON.stringify(crop);
  }
};

/**
 * Compare images and offCanvasImages between two states by content only (id, x, y, width, height, rotation, src, cropData).
 * Uses rounded numbers and stable cropData so add_image and update_image_properties merge when user just added and unselected.
 */
function areImageStatesContentEqual(stateA, stateB) {
  const imgsA = stateA.images || [];
  const imgsB = stateB.images || [];
  const offA = stateA.offCanvasImages || [];
  const offB = stateB.offCanvasImages || [];
  if (imgsA.length !== imgsB.length || offA.length !== offB.length) return false;
  const imgContentKey = (i) =>
    `${i.id}-${round(i.x)}-${round(i.y)}-${round(i.width)}-${round(i.height)}-${round(i.rotation ?? 0)}-${i.src || ""}-${stableCropKey(i.cropData)}`;
  const sortedById = (arr) => [...arr].sort((a, b) => (a.id || "").localeCompare(b.id || ""));
  const aMain = sortedById(imgsA).map(imgContentKey).join("|");
  const bMain = sortedById(imgsB).map(imgContentKey).join("|");
  if (aMain !== bMain) return false;
  const aOff = sortedById(offA).map(imgContentKey).join("|");
  const bOff = sortedById(offB).map(imgContentKey).join("|");
  return aOff === bOff;
}

/**
 * Saves the current canvas state to history for undo/redo functionality
 * @param {Object} params - Parameters for saving history
 * @param {string} params.operationType - Type of operation (e.g., "add_text", "delete_object")
 * @param {Array|null} params.currentImagesOverride - Override images array (optional)
 * @param {Array|null} params.currentArrowsOverride - Override arrows array (optional)
 * @param {Array|null} params.currentDoubleSidedArrowsOverride - Override double-sided arrows array (optional)
 * @param {Array|null} params.currentLinesOverride - Override lines array (optional)
 * @param {Array|null} params.currentTextsOverride - Override texts array (optional)
 * @param {Array} params.images - Current images array
 * @param {Array} params.texts - Current texts array
 * @param {Array} params.rectangles - Current rectangles array
 * @param {Array} params.circles - Current circles array
 * @param {Array} params.arrows - Current arrows array
 * @param {Array} params.lines - Current lines array
 * @param {Array} params.doubleSidedArrows - Current double-sided arrows array
 * @param {Array} params.videos - Current videos array
 * @param {Array} params.scribbles - Current scribbles array
 * @param {Array} params.offCanvasTexts - Current off-canvas texts array
 * @param {Array} params.offCanvasRectangles - Current off-canvas rectangles array
 * @param {Array} params.offCanvasCircles - Current off-canvas circles array
 * @param {Array} params.offCanvasArrows - Current off-canvas arrows array
 * @param {Array} params.offCanvasDoubleSidedArrows - Current off-canvas double-sided arrows array
 * @param {Array} params.offCanvasImages - Current off-canvas images array
 * @param {Array} params.offCanvasScribbles - Current off-canvas scribbles array
 * @param {Array} params.offCanvasLines - Current off-canvas lines array
 * @param {Array} params.offCanvasVideos - Current off-canvas videos array
 * @param {Object|null} params.selectedText - Currently selected text object
 * @param {string|null} params.selectedTextId - Currently selected text ID
 * @param {string} params.canvasHeight - Current canvas height (as percentage string)
 * @param {Array} params.history - Current history array
 * @param {Function} params.setHistory - Function to set history state
 * @param {Function} params.setRedoStack - Function to set redo stack state
 * @param {Function} params.setPendingChanges - Function to set pending changes flag
 * @param {React.RefObject} params.pendingChangesRef - Ref for pending changes (set synchronously when deferring so endElementEditing sees it on unselect)
 * @param {React.RefObject} params.isUndoingRedoing - Ref to track if undo/redo is in progress
 * @param {React.RefObject} params.lastUndoRedoCompletionTime - Ref to track when undo/redo last completed
 * @param {boolean} params.isElementBeingEdited - Flag indicating if element is being edited
 * @param {React.RefObject} params.lastSaveTimestamp - Ref to track last save timestamp
 * @param {React.RefObject} params.saveDebounceTimeout - Ref to track debounce timeout
 * @param {number} params.MAX_HISTORY_SIZE - Maximum history size (default: Infinity)
 */
export function saveToHistory({
  operationType = "general",
  currentImagesOverride = null,
  currentArrowsOverride = null,
  currentDoubleSidedArrowsOverride = null,
  currentLinesOverride = null,
  currentTextsOverride = null,
  currentSelectedTextOverride = null,
  currentSelectedTextIdOverride = null,
  currentRectanglesOverride = null,
  currentOffCanvasRectanglesOverride = null,
  currentCirclesOverride = null,
  currentOffCanvasCirclesOverride = null,
  currentOffCanvasArrowsOverride = null,
  currentOffCanvasDoubleSidedArrowsOverride = null,
  currentOffCanvasLinesOverride = null,
  currentOffCanvasTextsOverride = null,
  currentOffCanvasImagesOverride = null,
  deletedObjectId = null, // ID of the element that was deleted (for delete_object operations)
  deletedObjectType = null, // Type of the element that was deleted (for delete_object operations)
  images,
  texts,
  rectangles,
  circles,
  arrows,
  lines,
  doubleSidedArrows,
  videos,
  scribbles,
  offCanvasTexts,
  offCanvasRectangles,
  offCanvasCircles,
  offCanvasArrows,
  offCanvasDoubleSidedArrows,
  offCanvasImages,
  offCanvasScribbles,
  offCanvasLines,
  offCanvasVideos,
  selectedText,
  selectedTextId,
  canvasHeight,
  history,
  setHistory,
  setRedoStack,
  setPendingChanges,
  pendingChangesRef = null, // When we defer, set ref so endElementEditing sees pending changes on unselect
  isUndoingRedoing,
  lastUndoRedoCompletionTime,
  isElementBeingEdited,
  lastSaveTimestamp,
  saveDebounceTimeout,
  MAX_HISTORY_SIZE = Infinity,
  commitPendingHeightChange = null, // Function to commit pending canvas height changes
  hasPendingHeightChange = () => false, // Function to check if there's a pending height change
  currentCanvasHeightOverride = null, // For canvas_height_change: use this so orb/editor save correct height
  lastHistoryOperationTypeRef = null, // Ref updated when we push so endElementEditing can skip redundant save-from-ref
}) {
  // Check if we're currently undoing/redoing to prevent infinite loops
  if (isUndoingRedoing.current) {
    if (operationType === "cut_image") {
    }
    if (operationType === "before_drag_transfer") {
    }
    return;
  }

  // DEBUG: Log before_drag_transfer operations
  if (operationType === "before_drag_transfer") {
  }

  // DEBUG: Log drag_transfer operations
  if (operationType === "drag_transfer") {
  }

  // CRITICAL: Check if we're in the grace period after before_drag_transfer
  // If so, allow drag_transfer to proceed immediately without waiting
  const BEFORE_DRAG_TRANSFER_GRACE_PERIOD_MS = 500; // 500ms grace period
  if (history && history.length > 0) {
    const lastEntry = history[history.length - 1];
    const lastEntryOperationType = lastEntry?.operationType;
    const lastEntryTimestamp = lastEntry?.timestamp || 0;
    const checkTime = Date.now();
    const timeSinceLastOperation = checkTime - lastEntryTimestamp;

    // If last operation was before_drag_transfer and we're within grace period,
    // and current operation is drag_transfer, allow it to proceed
    if (
      lastEntryOperationType === "before_drag_transfer" &&
      timeSinceLastOperation < BEFORE_DRAG_TRANSFER_GRACE_PERIOD_MS &&
      operationType === "drag_transfer"
    ) {
      // Allow drag_transfer to proceed - don't return early
    }
  }

  // CRITICAL: Always defer add_text_to_shape and remove_text_from_shape - they are property changes, not structural additions
  // These should ALWAYS be grouped with other property changes via element_edit_complete
  // Check this BEFORE the isElementBeingEdited block to ensure it's always deferred
  const isTextShapeOperation =
    operationType === "add_text_to_shape" ||
    operationType === "remove_text_from_shape";

  // CRITICAL: Always defer clone operations when element is being edited
  // Clone operations should be grouped with other edits during an editing session
  const isCloneOperation =
    operationType === "add_rectangle_clone" ||
    operationType === "add_circle_clone" ||
    operationType === "add_image_clone" ||
    operationType === "add_text_clone" ||
    operationType === "add_arrow_clone" ||
    operationType === "add_line_clone" ||
    operationType === "add_double_sided_arrow_clone" ||
    operationType.includes("_clone");

  // CRITICAL: Always defer text shape operations - they should never save immediately
  // They are property changes and should be grouped with other edits (color, dimensions, etc.)
  // This ensures a single undo reverts all changes made during an editing session
  if (isTextShapeOperation && operationType !== "element_edit_complete") {
    // Set pendingChanges to ensure element_edit_complete will save everything together
    if (setPendingChanges) {
      setPendingChanges(true);
    }
    return; // Defer the save - element_edit_complete will save everything together
  }

  // CRITICAL: Always defer clone operations when element is being edited
  // This ensures all clones created during an editing session are grouped into one history entry
  if (isCloneOperation && isElementBeingEdited && operationType !== "element_edit_complete") {
    // Set pendingChanges to ensure element_edit_complete will save everything together
    if (setPendingChanges) {
      setPendingChanges(true);
    }
    return; // Defer the save - element_edit_complete will save everything together
  }

  // CRITICAL: Log if clone operations are being saved immediately (this should not happen during editing)
  if (isCloneOperation && !isElementBeingEdited) {
    console.warn("⚠️ [HISTORY] Clone operation being saved immediately - element not being edited!", {
      operationType,
      isElementBeingEdited,
      reason: "This might cause clones to appear as separate history entries",
      timestamp: Date.now(),
    });
  }

  // ADDITIONAL SAFEGUARD: Always defer clone operations, even if isElementBeingEdited is false
  // This ensures clone operations are always grouped, regardless of timing issues
  // This is a fallback to handle edge cases where isElementBeingEdited might not be set correctly
  if (isCloneOperation && operationType !== "element_edit_complete") {
    // Set pendingChanges to ensure element_edit_complete will save everything together
    if (setPendingChanges) {
      setPendingChanges(true);
    }
    return; // Always defer clone operations
  }

  // CRITICAL: After a delete_object or add_part operation, prevent any subsequent saveToHistory calls
  // from clearing or modifying the history for a short grace period
  // This prevents state updates from deletion (like setSelectedObject(null)) or unselection
  // from triggering additional history saves that might overwrite the delete_object or add_part entry
  const DELETE_GRACE_PERIOD_MS = 1000; // 1000ms grace period after deletion (increased from 500ms)
  const ADD_PART_GRACE_PERIOD_MS = 2000; // Increased to 2000ms to prevent element_edit_complete from overwriting add_part // 1000ms grace period after adding a part
  const CUT_IMAGE_GRACE_PERIOD_MS = 2000; // 2000ms grace period after cut_image to prevent subsequent operations from interfering
  if (history && history.length > 0) {
    const lastEntry = history[history.length - 1];
    const lastEntryOperationType = lastEntry?.operationType;
    const lastEntryTimestamp = lastEntry?.timestamp || 0;
    const checkTime = Date.now();
    const timeSinceLastOperation = checkTime - lastEntryTimestamp;

    // Check for delete_object / delete_multiple_objects grace period
    if (
      lastEntryOperationType === "delete_object" ||
      lastEntryOperationType === "delete_multiple_objects"
    ) {
      if (
        timeSinceLastOperation < DELETE_GRACE_PERIOD_MS &&
        operationType !== "delete_object" &&
        operationType !== "delete_multiple_objects"
      ) {
        // Still within grace period after delete_object - skip this save
        // This prevents state updates from deletion from overwriting the delete_object entry
        return;
      } else if (timeSinceLastOperation < DELETE_GRACE_PERIOD_MS) {
        // Log when we're in grace period but allowing delete_object itself
      }
    }

    // Check for add_part grace period
    if (lastEntryOperationType === "add_part") {
      if (
        timeSinceLastOperation < ADD_PART_GRACE_PERIOD_MS &&
        operationType !== "add_part"
      ) {
        // Still within grace period after add_part - skip this save
        // This prevents state updates (including element_edit_complete) from overwriting the add_part entry
        // CRITICAL: Also skip element_edit_complete to prevent it from clearing history after add_part
        if (operationType === "cut_image") {
        }
        return;
      }
    }

    // Check for cut_image grace period
    if (lastEntryOperationType === "cut_image") {
      if (
        timeSinceLastOperation < CUT_IMAGE_GRACE_PERIOD_MS &&
        operationType !== "cut_image"
      ) {
        // Still within grace period after cut_image - skip this save
        // This prevents state updates (like setSelectedObject(null)) from overwriting the cut_image entry
        return;
      } else if (timeSinceLastOperation < CUT_IMAGE_GRACE_PERIOD_MS) {
        // Log when we're in grace period but allowing cut_image itself
      }
    }
  }

  // Define explicit operations list early for use in debug logs and checks
  const explicitOperationsList = [
    "add_text",
    "add_image",
    "add_video", // Video addition
    "delete_object",
    "delete_multiple_objects",
    "element_edit_complete",
    "clear_canvas",
    "add_part",
    "replacePart",
    "add_rectangle",
    "add_circle",
    "add_line",
    "add_arrow",
    "add_double_sided_arrow",
    "add_scribble",
    // Clone operations - these should be treated as explicit but deferred when editing
    "add_rectangle_clone",
    "add_circle_clone",
    "add_image_clone",
    "add_text_clone",
    "add_arrow_clone",
    "add_line_clone",
    "add_double_sided_arrow_clone",
    "canvas_height_change",
    "arrow_drag_end",
    "line_drag_end",
    "line_drag_end_off_canvas", // Off-canvas line drag: save immediately so undo doesn't collapse with main add
    "rectangle_drag_end",
    "circle_drag_end",
    "double_sided_arrow_drag_end",
    "text_drag_end",
    "image_drag_end",
    "video_drag_end", // Video drag end
    "scribble_drag_end", // Scribble drag end
    "update_text_background",
    "update_text_content",
    "update_text_properties",
    "add_text_to_shape",
    "remove_text_from_shape",
    "import_canvas",
    "update_image_properties",
    "update_rectangle_properties",
    "update_circle_properties",
    "update_arrow_properties", // Arrow property updates
    "update_line_properties", // Line property updates
    "update_double_sided_arrow_properties", // Double-sided arrow property updates
    "update_video_properties", // Video property updates
    "drag_transfer", // Element transfer between main and off-canvas
    "before_drag_transfer", // State before element transfer (for undo)
    "add_preset_image", // Preset image addition
    "replace_image", // Image replacement
    "copy_cropped_image", // Copy cropped image operation
    "copy_image", // Copy image operation
    "cut_image", // Cut image operation
  ];
  const isExplicitOperation = explicitOperationsList.includes(operationType);

  // DEBUG: Log when saveToHistory is called (before any checks)
  const currentHistoryLength = history?.length || 0;
  const lastHistoryEntry =
    history && history.length > 0 ? history[history.length - 1] : null;

  // Grace period after undo/redo completion to prevent clearing redo stack too early
  // This protects against race conditions where state changes from undo/redo restoration
  // trigger saveToHistory before the undo/redo operation fully completes
  const UNDO_REDO_GRACE_PERIOD_MS = 300; // 300ms grace period
  const now = Date.now();
  if (
    lastUndoRedoCompletionTime &&
    lastUndoRedoCompletionTime.current &&
    now - lastUndoRedoCompletionTime.current < UNDO_REDO_GRACE_PERIOD_MS
  ) {
    // Still within grace period after undo/redo - skip side-effect saves
    // Skip non-explicit operations that might be side effects of undo/redo
    // CRITICAL: Also skip update_image_properties - it is often triggered by setOffCanvasImages
    // during undo restore (image re-render), which would add a spurious history entry and leave
    // undo button enabled after undoing off-canvas image
    if (!isExplicitOperation || operationType === "update_image_properties") {
      return;
    }
  }

  // Handle grouped undo/redo - defer saving when element is being edited
  // Only save immediately if it's the final "element_edit_complete" operation
  // BUT: Always save explicit add operations (shape creation) immediately, even if editing
  // Also allow structural text changes (add/remove text from shape) to proceed immediately
  // BUT: Defer property changes (color, content, etc.) when editing - they'll be saved on unselect
  // CRITICAL: Transform operations (update_rectangle_properties, update_circle_properties) should
  // always save immediately, not be deferred, because they represent user transform actions
  // This ensures each transform gets its own history entry
  // CRITICAL: Drag_end operations should also always save immediately, even when editing
  // This ensures drag operations create history entries that can be undone

  if (isElementBeingEdited && operationType !== "element_edit_complete") {
    // Allow explicit add operations to proceed immediately
    // NOTE: add_text_to_shape and remove_text_from_shape are NOT included here
    // They should be grouped with other property changes when element is being edited
    // CRITICAL: Exclude add_text_to_shape and remove_text_from_shape from isAddOperation
    // These are property changes, not structural additions, and should be grouped with other edits
    // CRITICAL: Exclude clone operations from immediate saves when editing
    // Clone operations should be grouped with other property changes via element_edit_complete
    const isAddOperation = (operationType.startsWith("add_") &&
      operationType !== "add_text_to_shape" &&
      !operationType.includes("_clone")) ||
      operationType === "copy_cropped_image" ||
      operationType === "copy_image";
    // Exclude add_text_to_shape and remove_text_from_shape from immediate saves when editing
    // These should be grouped with other property changes via element_edit_complete
    const isStructuralTextChange = false; // Disabled - text add/remove should be grouped with property changes
    // Transform operations should always save immediately (not deferred)
    // EXCEPT line property updates while an element is being edited – those should be grouped
    // and saved via element_edit_complete so a single undo/redo restores the full line state
    // (including both arrow ends) symmetrically.
    const isTransformOperation =
      operationType === "update_rectangle_properties" ||
      operationType === "update_circle_properties" ||
      operationType === "update_image_properties" ||
      operationType === "update_arrow_properties" ||
      (operationType === "update_line_properties" &&
        !isElementBeingEdited) ||
      operationType === "update_double_sided_arrow_properties" ||
      operationType === "update_video_properties" ||
      operationType === "nudge_selected_elements";
    // CRITICAL: delete_object and delete_multiple_objects should always save immediately (not deferred)
    const isDeleteOperation =
      operationType === "delete_object" ||
      operationType === "delete_multiple_objects";
    // CRITICAL: cut_image operations should always save immediately (not deferred)
    const isCutOperation = operationType === "cut_image";
    // CRITICAL: add_part should always save immediately when editing (so undo works for add).
    // replacePart is NOT included: it is deferred like other text edits and saved on unselection
    // (element_edit_complete), so history behavior matches add_part (save occurs after unselection).
    const isReplacePartOrAddPart = operationType === "add_part";
    // When element is selected: defer drag_end, before_drag_transfer, drag_transfer so they group with
    // the rest of the edit (move, resize, rotate) and one history save happens on unselection.
    // line_drag_end_off_canvas is NOT included so it is never deferred - we need it saved immediately
    const isDragEndOrTransfer =
      operationType === "image_drag_end" ||
      operationType === "arrow_drag_end" ||
      operationType === "line_drag_end" ||
      operationType === "rectangle_drag_end" ||
      operationType === "circle_drag_end" ||
      operationType === "double_sided_arrow_drag_end" ||
      operationType === "text_drag_end" ||
      operationType === "video_drag_end" ||
      operationType === "scribble_drag_end" ||
      operationType === "before_drag_transfer" ||
      operationType === "drag_transfer";
    // line_drag_end_off_canvas must proceed to save (not defer) so off-canvas line drag gets its own history entry
    const isOffCanvasLineDragSave = operationType === "line_drag_end_off_canvas";
    if (
      !isAddOperation &&
      !isStructuralTextChange &&
      !isTransformOperation &&
      !isDeleteOperation &&
      !isCutOperation &&
      !isReplacePartOrAddPart &&
      !isDragEndOrTransfer &&
      !isOffCanvasLineDragSave
    ) {
      // Defer property changes when editing (color, content, etc.)
      if (setPendingChanges) setPendingChanges(true);
      if (pendingChangesRef) pendingChangesRef.current = true; // So endElementEditing sees it on unselect (ref is read synchronously)
      return;
    }
    // When we get here with isDragEndOrTransfer while editing: defer so selection → changes → unselect = one save
    if (isDragEndOrTransfer) {
      setPendingChanges(true);
      return;
    }
    // For add, transform, delete, cut: proceed with saving even if editing
  }

  // Skip "general" operationType saves - these are usually side effects of state changes
  // that shouldn't create history entries (like selection changes, re-renders, etc.)
  // Only allow "general" if it's an explicit operation (which it shouldn't be)
  if (operationType === "general" && !isExplicitOperation) {
    // Skip non-explicit "general" operations to prevent unnecessary history entries
    if (operationType === "before_drag_transfer") {
    }
    return;
  }

  // If this IS a canvas_height_change operation, check if we should skip it
  // (it might be from arrow keys that should be grouped)
  // Skip canvas_height_change only when there's a pending height change from arrow keys AND we don't have an explicit override (orb/editor commit)
  if (operationType === "canvas_height_change") {
    if (currentCanvasHeightOverride == null && typeof hasPendingHeightChange === "function") {
      const hasPending = hasPendingHeightChange();
      if (hasPending) {
        // This is from arrow keys or input field - skip saving here
        // The commitPendingHeightChange will be called when another operation occurs
        return;
      }
    }
  }

  // If this is an explicit operation (not canvas_height_change), commit any pending height changes first
  // This groups multiple height changes (from arrow keys) into one history entry
  // IMPORTANT: Commit BEFORE building currentState so the height change uses the state BEFORE this operation
  if (
    isExplicitOperation &&
    operationType !== "canvas_height_change" &&
    typeof commitPendingHeightChange === "function"
  ) {
    // Get the last history state to use for the height change commit
    // This ensures the height change is saved with the state BEFORE the current operation
    const lastHistoryState =
      history.length > 0 ? history[history.length - 1] : null;

    // Commit with state overrides from the last history entry
    // This ensures the height change entry doesn't include elements added in the current operation
    if (lastHistoryState) {
      commitPendingHeightChange({
        currentRectanglesOverride: lastHistoryState.rectangles,
        currentCirclesOverride: lastHistoryState.circles,
        currentArrowsOverride: lastHistoryState.arrows,
        currentDoubleSidedArrowsOverride: lastHistoryState.doubleSidedArrows,
        currentLinesOverride: lastHistoryState.lines,
        currentTextsOverride: lastHistoryState.texts,
        currentImagesOverride: lastHistoryState.images,
        currentOffCanvasRectanglesOverride:
          lastHistoryState.offCanvasRectangles,
        currentOffCanvasCirclesOverride: lastHistoryState.offCanvasCircles,
        currentOffCanvasArrowsOverride: lastHistoryState.offCanvasArrows,
        currentOffCanvasDoubleSidedArrowsOverride:
          lastHistoryState.offCanvasDoubleSidedArrows,
        currentOffCanvasLinesOverride: lastHistoryState.offCanvasLines,
        currentOffCanvasTextsOverride: lastHistoryState.offCanvasTexts,
        currentOffCanvasImagesOverride: lastHistoryState.offCanvasImages,
        currentSelectedTextOverride: lastHistoryState.selectedText,
        currentSelectedTextIdOverride: lastHistoryState.selectedTextId,
      });
    } else {
      // No previous state, commit without overrides
      commitPendingHeightChange();
    }
  }

  // Prevent rapid duplicate saves (debounce to 100ms except for explicit operations)
  // isExplicitOperation is already defined above

  if (!isExplicitOperation && now - lastSaveTimestamp.current < 100) {
    if (operationType === "before_drag_transfer") {
    }
    if (operationType === "drag_transfer") {
    }
    // Clear any pending debounce
    if (saveDebounceTimeout.current) {
      clearTimeout(saveDebounceTimeout.current);
    }
    // Debounce the save - note: this creates a recursive call
    // The wrapper function in MyCanvas.js will handle the debouncing properly
    saveDebounceTimeout.current = setTimeout(() => {
      saveToHistory({
        operationType,
        currentImagesOverride,
        currentArrowsOverride,
        currentDoubleSidedArrowsOverride,
        currentLinesOverride,
        currentTextsOverride,
        images,
        texts,
        rectangles,
        circles,
        arrows,
        lines,
        doubleSidedArrows,
        videos,
        scribbles,
        offCanvasTexts,
        offCanvasRectangles,
        offCanvasCircles,
        offCanvasArrows,
        offCanvasDoubleSidedArrows,
        offCanvasImages,
        offCanvasScribbles,
        offCanvasLines,
        offCanvasVideos,
        selectedText,
        selectedTextId,
        history,
        setHistory,
        setRedoStack,
        setPendingChanges,
        isUndoingRedoing,
        isElementBeingEdited,
        lastSaveTimestamp,
        saveDebounceTimeout,
        MAX_HISTORY_SIZE,
      });
    }, 100);
    return;
  }

  lastSaveTimestamp.current = now;

  // CRITICAL: When transform operations save, clear pendingChanges flag AND set a ref to track it
  // This prevents element_edit_complete from also saving and creating duplicate entries
  // Transform operations (update_rectangle_properties, update_circle_properties, update_image_properties, etc.)
  // represent immediate user actions that should be saved immediately, not grouped
  const isTransformOperation =
    operationType === "update_rectangle_properties" ||
    operationType === "update_circle_properties" ||
    operationType === "update_image_properties" ||
    operationType === "update_arrow_properties" ||
    operationType === "update_line_properties" ||
    operationType === "update_double_sided_arrow_properties" ||
    operationType === "update_video_properties";

  if (isTransformOperation) {
    // Clear pendingChanges so element_edit_complete doesn't also save
    if (typeof setPendingChanges === "function") {
      setPendingChanges(false);
    }
    // Update lastSaveTimestamp to track when transform was saved
    // This helps the element_edit_complete check work correctly
    lastSaveTimestamp.current = now;
  }

  // CRITICAL: Log before try block to see if we reach this point
  if (operationType === "delete_object") {
  }

  try {
    // DEBUG: Log at start of try block to ensure we reach this point
    if (operationType === "delete_object") {
    }

    // Process images to store only essential data (no heavy data URLs for history)
    const imagesToSerialize = currentImagesOverride || images;

    // DEBUG: Log for cut_image to verify images are being serialized
    if (operationType === "cut_image") {
    }
    const serializedImages = imagesToSerialize.map((img) => {
      // Store only essential image metadata - not full data URLs to save memory
      // CRITICAL: Explicitly check for rotation, scaleX, scaleY to ensure they're preserved
      // Use nullish coalescing (??) instead of || to preserve 0 values
      const rotation = img.rotation ?? 0;
      const scaleX = img.scaleX ?? 1;
      const scaleY = img.scaleY ?? 1;

      const serializedImg = {
        id: img.id,
        x: img.x || 0,
        y: img.y || 0,
        width: img.width || 100,
        height: img.height || 100,
        src: img.src, // Keep src reference only
        maintainAspectRatio:
          img.maintainAspectRatio !== undefined
            ? img.maintainAspectRatio
            : true,
        compress: img.compress || false,
        imageNumber: img.imageNumber,
        name: img.name,
        rotation: rotation, // Use explicitly calculated rotation
        scaleX: scaleX, // Use explicitly calculated scaleX
        scaleY: scaleY, // Use explicitly calculated scaleY
        isOnTopLayer: !!img.isOnTopLayer,
        // CRITICAL FIX: Store useTopLeftAnchor for proper positioning during undo/redo
        useTopLeftAnchor: img.useTopLeftAnchor === true,
        // CRITICAL FIX: Store actual data URLs for undo/redo to work with crop modal
        originalImageDataUrl: img.originalImageDataUrl || null,
        imageDataUrl: img.imageDataUrl || null,
        cropData: img.cropData, // Keep crop data
        // Store additional crop-related properties
        originalCropRotation: img.originalCropRotation || 0,
        flipHorizontal: img.flipHorizontal || false,
        flipVertical: img.flipVertical || false,
        hasBakedTransforms: img.hasBakedTransforms || false,
        originalWidth: img.originalWidth,
        originalHeight: img.originalHeight,
        originalAspectRatio: img.originalAspectRatio,
        referenceUrl: img.referenceUrl || "", // <--- FIX: Save referenceUrl for undo/redo
      };

      // Use basic spread instead of structuredClone for better performance
      const finalImg = { ...serializedImg };

      // Debug logging for transform operations to verify rotation is being saved
      if (isTransformOperation && operationType === "update_image_properties") {
      }

      // Debug logging for cut operations to verify cropped data is being saved
      if (operationType === "cut_image") {
      }

      return finalImg;
    });

    // Create current state - use lightweight copies for memory efficiency
    // CRITICAL: For delete_object, ALWAYS use the override if provided

    const isDeleteOp = operationType === "delete_object" || operationType === "delete_multiple_objects";
    const rectsToSave =
      isDeleteOp && currentRectanglesOverride
        ? currentRectanglesOverride // Force use override for delete operations
        : currentRectanglesOverride || rectangles;

    // CRITICAL: Log for delete_object to debug why rectangles aren't being saved
    if (operationType === "delete_object") {

      // CRITICAL: Error if override is empty for delete_object
      if (
        !currentRectanglesOverride ||
        currentRectanglesOverride.length === 0
      ) {
        console.error(
          "❌ [HISTORY] ERROR: currentRectanglesOverride is empty for delete_object!",
          {
            currentRectanglesOverride,
            rectangles,
            rectsToSave,
          }
        );
      }
    }

    const currentState = {
      texts: JSON.parse(JSON.stringify(currentTextsOverride || texts)),
      selectedText: currentSelectedTextOverride
        ? JSON.parse(JSON.stringify(currentSelectedTextOverride))
        : selectedText
          ? JSON.parse(JSON.stringify(selectedText))
          : null, // Save selectedText state
      selectedTextId: currentSelectedTextIdOverride || selectedTextId, // Save selectedTextId
      rectangles: JSON.parse(JSON.stringify(rectsToSave)),
      circles: JSON.parse(JSON.stringify(currentCirclesOverride || circles)),
      arrows: (() => {
        const inputArrows = currentArrowsOverride || arrows;

        // DEBUG: Log circles being saved for add_rectangle operations
        if (operationType === "add_rectangle" || operationType === "add_rectangle_clone") {
        }

        // Check if any arrows are being restored
        const hasRestoredArrows = inputArrows.some(
          (arrow) => arrow.isRestored || arrow.isFromHistory
        );

        if (hasRestoredArrows) {
          return JSON.parse(JSON.stringify(inputArrows));
        }

        const normalizedArrows = normalizeArrowCoordinates(inputArrows);
        return JSON.parse(JSON.stringify(normalizedArrows));
      })(),
      doubleSidedArrows: (() => {
        const inputDoubleSidedArrows =
          currentDoubleSidedArrowsOverride || doubleSidedArrows;

        // Check if any arrows are being restored
        const hasRestoredArrows = inputDoubleSidedArrows.some(
          (arrow) => arrow.isRestored || arrow.isFromHistory
        );

        if (hasRestoredArrows) {
          return JSON.parse(JSON.stringify(inputDoubleSidedArrows));
        }

        const normalizedDoubleSidedArrows = normalizeArrowCoordinates(
          inputDoubleSidedArrows
        );
        return JSON.parse(JSON.stringify(normalizedDoubleSidedArrows));
      })(),
      images: serializedImages,
      videos: JSON.parse(JSON.stringify(videos)), // Include videos in history
      scribbles: JSON.parse(JSON.stringify(scribbles)),
      lines: JSON.parse(JSON.stringify(currentLinesOverride || lines)),
      // Offcanvas elements - store minimal data to save memory
      offCanvasTexts: JSON.parse(
        JSON.stringify(currentOffCanvasTextsOverride || offCanvasTexts)
      ),
      offCanvasRectangles: (() => {
        const offCanvasRects = currentOffCanvasRectanglesOverride || offCanvasRectangles;
        return JSON.parse(JSON.stringify(offCanvasRects));
      })(),
      offCanvasCircles: JSON.parse(
        JSON.stringify(currentOffCanvasCirclesOverride || offCanvasCircles)
      ),
      offCanvasArrows: JSON.parse(
        JSON.stringify(currentOffCanvasArrowsOverride || offCanvasArrows)
      ),
      offCanvasDoubleSidedArrows: JSON.parse(
        JSON.stringify(
          currentOffCanvasDoubleSidedArrowsOverride ||
          offCanvasDoubleSidedArrows
        )
      ),
      offCanvasImages: JSON.parse(
        JSON.stringify(currentOffCanvasImagesOverride || offCanvasImages)
      ),
      offCanvasScribbles: JSON.parse(JSON.stringify(offCanvasScribbles)),
      offCanvasLines: JSON.parse(
        JSON.stringify(currentOffCanvasLinesOverride || offCanvasLines)
      ),
      offCanvasVideos: JSON.parse(JSON.stringify(offCanvasVideos)),
      canvasHeight: (operationType === "canvas_height_change" && currentCanvasHeightOverride != null)
        ? String(currentCanvasHeightOverride)
        : (canvasHeight || "80"), // Save canvas height for undo/redo
      operationType: operationType, // Track the operation type for better undo/redo
      timestamp: now, // Add timestamp to track when this state was saved
      // Store deleted object info for delete operations (needed for redo)
      deletedObjectId:
        isDeleteOp ? deletedObjectId : undefined,
      deletedObjectType:
        isDeleteOp ? deletedObjectType : undefined,
    };

    // Use functional update form of setHistory to always get the latest history state
    // This is critical when multiple saveToHistory calls happen quickly (e.g., locked tools)
    // All rapid calls will see the correct previous state, not stale closure values
    // Use flushSync to force immediate execution and prevent React from batching updates
    // CRITICAL: Wrap in try-catch to ensure errors don't prevent history saves
    try {
      // DEBUG: Log before flushSync to track if image_drag_end reaches this point
      if (operationType === "image_drag_end") {
      }

      if (operationType === "add_image" || operationType === "add_image_clone") {
      }

      // DEBUG: Log before flushSync for before_drag_transfer
      if (operationType === "before_drag_transfer") {
      }

      // DEBUG: Log before flushSync for drag_transfer
      if (operationType === "drag_transfer") {
      }

      flushSync(() => {
        setHistory((prev) => {
          // CRITICAL: When at initial state, only allow structural operations (add/delete/clear) or canvas_height_change.
          // Block everything else so deferred callbacks after undo don't add a spurious entry and leave undo enabled.
          // CRITICAL: Also allow element_edit_complete and drag_end operations at initial state so editing existing elements
          // creates history entries (otherwise undo button stays disabled when editing without adding new elements first).
          const isAtInitial = prev.length === 1 && prev[prev.length - 1]?.operationType === "initial";
          const isStructuralOp =
            (operationType && operationType.startsWith("add_")) ||
            operationType === "delete_object" ||
            operationType === "delete_multiple_objects" ||
            operationType === "clear_canvas" ||
            operationType === "import_canvas" ||
            operationType === "drag_transfer" ||
            operationType === "before_drag_transfer" ||
            operationType === "add_preset_image" ||
            operationType === "cut_image" ||
            operationType === "copy_image" ||
            operationType === "copy_cropped_image" ||
            operationType === "canvas_height_change" ||
            operationType === "element_edit_complete" ||
            operationType === "replacePart" ||
            operationType === "circle_drag_end" ||
            operationType === "rectangle_drag_end" ||
            operationType === "arrow_drag_end" ||
            operationType === "line_drag_end" ||
            operationType === "line_drag_end_off_canvas" ||
            operationType === "text_drag_end" ||
            operationType === "image_drag_end" ||
            operationType === "double_sided_arrow_drag_end";
          if (isAtInitial && !isStructuralOp) {
            return prev;
          }

          // DEBUG: Log before history update
          const prevRectanglesCount =
            prev.length > 0
              ? prev[prev.length - 1]?.rectangles?.length || 0
              : 0;
          const currentRectanglesCount = currentState.rectangles?.length || 0;
          const prevHistoryLength = prev.length;

          // DEBUG: Log for before_drag_transfer
          if (operationType === "before_drag_transfer") {
          }

          // If history is empty, initialize it with the current state
          if (prev.length === 0) {
            if (lastHistoryOperationTypeRef) lastHistoryOperationTypeRef.current = operationType;
            return [currentState];
          }

          // Get the last state from the latest history (not from closure)
          const lastState = prev[prev.length - 1];

          // Merge update_image_properties into add_image so one undo removes the image (matches off-canvas rectangles).
          // Off-canvas images use ImageElement's transformer; on unselect it can push update_image_properties from
          // multiple paths (mediaUtils setTimeout, shapeUpdateUtils, etc.). Always skip pushing when the previous
          // entry was add_image so history stays [initial, add_image] and one undo disables the button.
          if (
            operationType === "update_image_properties" &&
            (lastState?.operationType === "add_image" || lastState?.operationType === "add_image_clone")
          ) {
            return prev;
          }

          // CRITICAL: Skip element_edit_complete if the last entry was a transform operation, add_part, or another element_edit_complete
          // This prevents duplicate saves when element_edit_complete is called multiple times in quick succession
          // Transform operations and add_part already save immediately, so element_edit_complete is redundant
          // BUT: Only skip if the state hasn't changed - if state has changed, we need to save it
          if (operationType === "element_edit_complete" && lastState) {
            // Calculate hashes early to check if state has changed
            const currentHash = createStateHash(currentState);
            const lastHash = createStateHash(lastState);
            const hasStateChanged = currentHash !== lastHash;

            const lastOperationType = lastState.operationType;
            const TRANSFORM_COOLDOWN_MS = 500; // Increased to 500ms to handle setTimeout delays (50ms) + state updates
            const ADD_PART_COOLDOWN_MS = 2000; // Increased to 2000ms to prevent element_edit_complete from overwriting add_part
            const ELEMENT_EDIT_COMPLETE_COOLDOWN_MS = 300; // Cooldown to prevent duplicate element_edit_complete calls
            const timeSinceLastSave = now - (lastState.timestamp || 0);

            // Check if last operation was add_part
            const wasAddPartOperation = lastOperationType === "add_part";

            // Check if last operation was add_image/add_preset_image (redundant "save from ref" after adding image before unselect)
            const wasAddImageOperation =
              lastOperationType === "add_image" ||
              lastOperationType === "add_image_clone" ||
              lastOperationType === "add_preset_image";

            const ADD_IMAGE_COOLDOWN_MS = 800; // Skip or merge element_edit_complete right after add_image so 1 undo removes main image
            if (wasAddImageOperation) {
              // If element_edit_complete closely follows add_image, MERGE it into the add_image entry
              // so we keep the latest properties but still have a single undo step.
              if (timeSinceLastSave < ADD_IMAGE_COOLDOWN_MS) {
                const mergedHistory = [...prev];
                mergedHistory[mergedHistory.length - 1] = {
                  ...currentState,
                  operationType: lastOperationType,
                  timestamp: now,
                };
                return mergedHistory;
              }
            }

            // Check if last operation was another element_edit_complete (duplicate call)
            const wasElementEditComplete = lastOperationType === "element_edit_complete";

            // Also check if a transform was saved recently using lastSaveTimestamp
            // This handles the case where element_edit_complete is called with a setTimeout delay
            const timeSinceLastTransformSave = now - lastSaveTimestamp.current;

            const wasTransformOperation =
              lastOperationType === "update_rectangle_properties" ||
              lastOperationType === "update_circle_properties" ||
              lastOperationType === "update_image_properties" ||
              lastOperationType === "update_arrow_properties" ||
              lastOperationType === "update_line_properties" ||
              lastOperationType === "update_double_sided_arrow_properties" ||
              lastOperationType === "update_video_properties";

            // Check if last operation was a copy operation (these should allow element_edit_complete to save)
            const wasCopyOperation =
              lastOperationType === "copy_cropped_image" ||
              lastOperationType === "copy_image";

            // Skip if:
            // 1. Last save was another element_edit_complete (within cooldown period) - prevents duplicate calls, OR
            // 2. Last save was a transform operation (within cooldown period) AND state hasn't changed, OR
            // 3. A transform was saved very recently (within cooldown period) AND state hasn't changed, OR
            // 4. Last save was an add_part operation (within cooldown period), OR
            // 5. Last save was add_image/add_preset_image (within 800ms) - handled above with unconditional skip
            // BUT: Don't skip if last operation was a copy operation - copy operations should allow element_edit_complete
            // BUT: Don't skip if state has changed - we need to save the changes (e.g., text color changes)
            if (
              !wasCopyOperation &&
              !hasStateChanged &&
              ((timeSinceLastSave < ELEMENT_EDIT_COMPLETE_COOLDOWN_MS && wasElementEditComplete) ||
                (timeSinceLastSave < TRANSFORM_COOLDOWN_MS &&
                  wasTransformOperation) ||
                (timeSinceLastTransformSave < TRANSFORM_COOLDOWN_MS &&
                  wasTransformOperation) ||
                (timeSinceLastSave < ADD_PART_COOLDOWN_MS && wasAddPartOperation))
            ) {
              // Last save was a duplicate element_edit_complete, transform, or add_part operation - skip to avoid duplicate
              return prev; // Return unchanged history
            } else if (hasStateChanged && wasTransformOperation) {
              // State has changed even though last operation was a transform - this means property edits were deferred
              // We need to save element_edit_complete to capture the changes
            }
          }

          // Create a more granular comparison that detects specific changes
          // Calculate hashes early so they can be used in multiple checks
          const currentHash = createStateHash(currentState);
          const lastHash = createStateHash(lastState);

          // REMOVED: Previous check that was skipping element_edit_complete too aggressively
          // This check was causing element_edit_complete to be skipped even when there were pending changes
          // (like pointer head changes). The hash check below now properly handles element_edit_complete
          // by forcing save when hash matches (since element_edit_complete is only called when there
          // are pending changes). Additionally, the hash calculation now includes hasStartArrow and
          // hasEndArrow, so pointer head changes will be properly detected.

          // Use simple change detection - compare hashes
          // If hashes match, states are identical
          const hasSpecificChanges = currentHash !== lastHash;

          // CRITICAL: Always check if the new state is identical to the last state
          // If it is, don't save it to prevent consecutive duplicates in history
          // This applies to ALL operations, including transform operations
          // Even transform operations should be skipped if the state is truly identical
          // (e.g., when updateImageProperties is called multiple times with the same state during continuous transforms)
          // EXCEPTION: For drag_end operations, always save to history even if state appears identical
          // This ensures that drag operations create history entries that can be undone
          const isDragEndOperation =
            operationType === "image_drag_end" ||
            operationType === "arrow_drag_end" ||
            operationType === "line_drag_end" ||
            operationType === "line_drag_end_off_canvas" ||
            operationType === "rectangle_drag_end" ||
            operationType === "circle_drag_end" ||
            operationType === "double_sided_arrow_drag_end" ||
            operationType === "text_drag_end";

          // DEBUG: Log drag_end operations early to see if they're being processed
          if (isDragEndOperation) {
          }

          // CRITICAL: For drag_end operations, save to history, but check if the last entry was also a drag_end
          // If the last entry was a drag_end with the same state, skip to prevent duplicate entries
          // This handles the case where image_drag_end is called twice (e.g., from React strict mode or multiple event handlers)
          if (isDragEndOperation) {
            // Check if the last history entry was also a drag_end operation with the same state
            const lastOperationType = lastState?.operationType;
            const isLastStateDragEnd =
              lastOperationType === "image_drag_end" ||
              lastOperationType === "arrow_drag_end" ||
              lastOperationType === "line_drag_end" ||
              lastOperationType === "line_drag_end_off_canvas" ||
              lastOperationType === "rectangle_drag_end" ||
              lastOperationType === "circle_drag_end" ||
              lastOperationType === "double_sided_arrow_drag_end" ||
              lastOperationType === "text_drag_end" ||
              lastOperationType === "video_drag_end" ||
              lastOperationType === "scribble_drag_end";

            // If the last state was also a drag_end and the state is identical, skip to prevent duplicates
            if (
              isLastStateDragEnd &&
              currentHash === lastHash &&
              !hasSpecificChanges
            ) {
              return prev; // Return unchanged history
            }

            // Force save drag_end operations that are not duplicates
            // Continue to save the state (don't return early)
          } else if (currentHash === lastHash && !hasSpecificChanges) {
            // State is identical to last state - don't save duplicate
            // This prevents duplicate saves during continuous transforms or when the same state is saved multiple times
            // But always save drag_end operations to ensure they can be undone
            // Skip element_edit_complete when state is identical to last add (image/preset) - avoids redundant entry
            // (e.g. "save from ref" after adding main image before unselect, which would require 2 undos to remove main image)
            if (
              operationType === "element_edit_complete" &&
              (lastState?.operationType === "add_image" ||
                lastState?.operationType === "add_image_clone" ||
                lastState?.operationType === "add_preset_image")
            ) {
              return prev;
            }
            // CRITICAL: For delete_object operations, we should NOT skip even if hash matches
            // because we're saving the state BEFORE deletion, which should always be different
            // CRITICAL: For add_part operations, we should NOT skip even if hash matches
            // Adding a part is a structural change that should always be saved
            // CRITICAL: For cut_image operations, we should NOT skip even if hash matches
            // Cutting an image is a structural change that should always be saved
            // CRITICAL: For element_edit_complete operations, we should NOT skip even if hash matches
            // because element_edit_complete is only called when there are pending changes
            // This ensures that property changes (like pointer heads) are saved even if the hash
            // calculation doesn't detect them (e.g., due to hash calculation bugs or missing fields)
            // CRITICAL: For before_drag_transfer operations, we should NOT skip even if hash matches
            // This saves the state before transferring an element between canvases, which is essential for undo
            // CRITICAL: For drag_transfer operations, we should NOT skip even if hash matches
            // This saves the state after transferring an element between canvases, which is essential for undo
            if (
              operationType === "delete_object" ||
              operationType === "delete_multiple_objects" ||
              operationType === "add_part" ||
              operationType === "replacePart" ||
              operationType === "cut_image" ||
              operationType === "element_edit_complete" ||
              operationType === "before_drag_transfer" ||
              operationType === "drag_transfer" ||
              operationType === "canvas_height_change"
            ) {
              // Force save delete_object/add_part/replacePart/cut_image/element_edit_complete/... even if hash matches
              // Continue to save the state (don't return early)
              if (operationType === "before_drag_transfer") {
              }
              if (operationType === "drag_transfer") {
              }
            } else {
              // CRITICAL: Clear redo stack for explicit operations even when state is duplicate
              // This ensures that user actions (like drag_end) clear the redo stack even if
              // the state didn't change (e.g., dragging an element back to its original position)
              if (isExplicitOperation && setRedoStack) {
                setRedoStack((prev) => {
                  // Clear redo stack when explicit user action is performed (even if state is duplicate)
                  return [];
                });
              }
              return prev; // Return unchanged history
            }
          }

          // For transform operations, log when they're being saved to help debug
          const isCurrentTransformOperation =
            operationType === "update_rectangle_properties" ||
            operationType === "update_circle_properties" ||
            operationType === "update_image_properties" ||
            operationType === "update_arrow_properties" ||
            operationType === "update_line_properties" ||
            operationType === "update_double_sided_arrow_properties" ||
            operationType === "update_video_properties";

          if (isCurrentTransformOperation) {
          }

          // For drag_end operations, log when they're being saved to help debug
          if (isDragEndOperation) {
          }

          // Clean up any existing consecutive duplicates before adding new state
          // Remove consecutive duplicate states from history
          // CRITICAL: Don't remove drag_end operations even if they appear duplicate
          // This ensures drag operations can always be undone
          // CRITICAL: Don't remove delete_object operations even if they appear duplicate
          // This ensures deletions can always be undone
          // CRITICAL: Don't remove states that come before delete_object, even if they have the same hash
          // This is because delete_object saves the state BEFORE deletion, which might match the previous state
          let cleanedHistory = [...prev];
          let hasDuplicates = true;
          let iterations = 0;
          const MAX_ITERATIONS = 10; // Safety limit

          while (
            hasDuplicates &&
            cleanedHistory.length > 1 &&
            iterations < MAX_ITERATIONS
          ) {
            iterations++;
            hasDuplicates = false;
            const newCleanedHistory = [];

            for (let i = 0; i < cleanedHistory.length; i++) {
              const currentStateInHistory = cleanedHistory[i];
              const nextStateInHistory = cleanedHistory[i + 1];

              if (nextStateInHistory) {
                const currentStateHash = createStateHash(currentStateInHistory);
                const nextStateHash = createStateHash(nextStateInHistory);

                // Check if either state is a drag_end operation
                const isCurrentDragEnd =
                  currentStateInHistory.operationType === "image_drag_end" ||
                  currentStateInHistory.operationType === "arrow_drag_end" ||
                  currentStateInHistory.operationType === "line_drag_end" ||
                  currentStateInHistory.operationType === "line_drag_end_off_canvas" ||
                  currentStateInHistory.operationType ===
                  "rectangle_drag_end" ||
                  currentStateInHistory.operationType === "circle_drag_end" ||
                  currentStateInHistory.operationType ===
                  "double_sided_arrow_drag_end" ||
                  currentStateInHistory.operationType === "text_drag_end" ||
                  currentStateInHistory.operationType === "video_drag_end" ||
                  currentStateInHistory.operationType === "scribble_drag_end";

                const isNextDragEnd =
                  nextStateInHistory.operationType === "image_drag_end" ||
                  nextStateInHistory.operationType === "arrow_drag_end" ||
                  nextStateInHistory.operationType === "line_drag_end" ||
                  nextStateInHistory.operationType === "line_drag_end_off_canvas" ||
                  nextStateInHistory.operationType === "rectangle_drag_end" ||
                  nextStateInHistory.operationType === "circle_drag_end" ||
                  nextStateInHistory.operationType ===
                  "double_sided_arrow_drag_end" ||
                  nextStateInHistory.operationType === "text_drag_end" ||
                  nextStateInHistory.operationType === "video_drag_end" ||
                  nextStateInHistory.operationType === "scribble_drag_end";

                // Check if either state is a delete operation (single or multiple)
                const isCurrentDelete =
                  currentStateInHistory.operationType === "delete_object" ||
                  currentStateInHistory.operationType === "delete_multiple_objects";
                const isNextDelete =
                  nextStateInHistory.operationType === "delete_object" ||
                  nextStateInHistory.operationType === "delete_multiple_objects";

                // Check if either state is an add_part operation
                const isCurrentAddPart =
                  currentStateInHistory.operationType === "add_part";
                const isNextAddPart =
                  nextStateInHistory.operationType === "add_part";

                // Check if either state is a before_drag_transfer operation
                const isCurrentBeforeDragTransfer =
                  currentStateInHistory.operationType === "before_drag_transfer";
                const isNextBeforeDragTransfer =
                  nextStateInHistory.operationType === "before_drag_transfer";

                // CRITICAL: Never remove delete_object operations, even if they appear duplicate
                // Also, never remove the state BEFORE a delete_object (e.g., add_rectangle before delete_object)
                // This ensures deletions can always be undone
                // CRITICAL: Never remove add_part operations, even if they appear duplicate
                // Adding a part is a structural change that should always be preserved
                // If the current state is followed by a delete_object or add_part with the same hash, keep both
                // because they represent different operations even if the state is identical
                if (currentStateHash === nextStateHash) {
                  // If next state is delete_object, keep both (don't skip)
                  if (isNextDelete) {
                    // Keep both states - the add_rectangle and the delete_object
                    // They have the same hash but represent different operations
                    newCleanedHistory.push(currentStateInHistory);
                    // Also add the delete_object state immediately
                    newCleanedHistory.push(nextStateInHistory);
                    i++; // Skip next state since we've already added it
                    continue;
                  }
                  // If next state is add_part, keep both (don't skip)
                  if (isNextAddPart) {
                    // Keep both states - they have the same hash but represent different operations
                    newCleanedHistory.push(currentStateInHistory);
                    // Also add the add_part state immediately
                    newCleanedHistory.push(nextStateInHistory);
                    i++; // Skip next state since we've already added it
                    continue;
                  }
                  // If current state is delete_object, keep it (don't skip)
                  if (isCurrentDelete) {
                    newCleanedHistory.push(currentStateInHistory);
                    continue;
                  }
                  // If current state is add_part, keep it (don't skip)
                  if (isCurrentAddPart) {
                    newCleanedHistory.push(currentStateInHistory);
                    continue;
                  }
                  // If next state is before_drag_transfer, keep both (don't skip)
                  if (isNextBeforeDragTransfer) {
                    // Keep both states - they have the same hash but represent different operations
                    newCleanedHistory.push(currentStateInHistory);
                    // Also add the before_drag_transfer state immediately
                    newCleanedHistory.push(nextStateInHistory);
                    i++; // Skip next state since we've already added it
                    continue;
                  }
                  // If current state is before_drag_transfer, keep it (don't skip)
                  if (isCurrentBeforeDragTransfer) {
                    newCleanedHistory.push(currentStateInHistory);
                    continue;
                  }
                  // If next state is drag_end, keep both (don't skip)
                  if (isNextDragEnd) {
                    newCleanedHistory.push(currentStateInHistory);
                    // Also add the drag_end state immediately
                    newCleanedHistory.push(nextStateInHistory);
                    i++; // Skip next state since we've already added it
                    continue;
                  }
                  // If current state is drag_end, keep it (don't skip)
                  if (isCurrentDragEnd) {
                    newCleanedHistory.push(currentStateInHistory);
                    continue;
                  }
                  // Otherwise, skip the duplicate next state
                  hasDuplicates = true;
                  newCleanedHistory.push(currentStateInHistory);
                  i++; // Skip next state
                  continue;
                }
              }

              // Add state if it's not a duplicate
              newCleanedHistory.push(currentStateInHistory);
            }

            if (hasDuplicates) {
              cleanedHistory = newCleanedHistory;
            }
          }

          // Use cleaned history instead of prev
          const historyToUse =
            cleanedHistory.length > 0 ? cleanedHistory : prev;

          // Add new state to history with size limit
          const newHistory = [...historyToUse, currentState];
          // Limit history size to prevent memory issues with large images
          const finalHistory =
            newHistory.length > MAX_HISTORY_SIZE
              ? newHistory.slice(newHistory.length - MAX_HISTORY_SIZE)
              : newHistory;

          // DEBUG: Log after history update
          const lastEntry = finalHistory[finalHistory.length - 1];

          // DEBUG: Log before_drag_transfer saves
          if (operationType === "before_drag_transfer") {
          }

          // CRITICAL: For delete_object, verify the state was saved correctly
          if (operationType === "delete_object") {
            const hasRectangles = (lastEntry?.rectangles?.length || 0) > 0;
            const hasCircles = (lastEntry?.circles?.length || 0) > 0;
            const hasArrows = (lastEntry?.arrows?.length || 0) > 0;
            const hasLines = (lastEntry?.lines?.length || 0) > 0;
            const hasTexts = (lastEntry?.texts?.length || 0) > 0;
            const hasImages = (lastEntry?.images?.length || 0) > 0;
            const hasAnyElements =
              hasRectangles ||
              hasCircles ||
              hasArrows ||
              hasLines ||
              hasTexts ||
              hasImages;

            if (!hasAnyElements) {
              console.error(
                "❌ [HISTORY] ERROR: delete_object entry saved with no elements!",
                {
                  lastEntry: {
                    operationType: lastEntry?.operationType,
                    rectanglesCount: lastEntry?.rectangles?.length || 0,
                    circlesCount: lastEntry?.circles?.length || 0,
                    arrowsCount: lastEntry?.arrows?.length || 0,
                    linesCount: lastEntry?.lines?.length || 0,
                    textsCount: lastEntry?.texts?.length || 0,
                    imagesCount: lastEntry?.images?.length || 0,
                  },
                  currentState: {
                    rectanglesCount: currentState.rectangles?.length || 0,
                    circlesCount: currentState.circles?.length || 0,
                    arrowsCount: currentState.arrows?.length || 0,
                    linesCount: currentState.lines?.length || 0,
                    textsCount: currentState.texts?.length || 0,
                    imagesCount: currentState.images?.length || 0,
                  },
                }
              );
            } else {
            }
          }

          if (lastHistoryOperationTypeRef) lastHistoryOperationTypeRef.current = operationType;
          return finalHistory;
        });
      });

      // Clear redo stack only when a new genuine user action is performed
      // This should NOT happen during undo/redo restoration or immediately after
      // The grace period check above already handles the timing protection
      setRedoStack((prev) => {
        // Clear redo stack when new action is performed (but not during undo/redo)
        return [];
      });
    } catch (error) {
      // Log errors to help debug why image_drag_end might not be saving
      console.error("❌ [HISTORY] Error in flushSync for saveToHistory", {
        operationType,
        error: error.message,
        stack: error.stack,
      });
    }
  } catch (error) {
    // Log outer errors
    console.error("❌ [HISTORY] Outer error in saveToHistory", {
      operationType,
      error: error.message,
      stack: error.stack,
    });
  }
}
